<template>
    <div>
        <v-row justify="center">
            <v-col cols="12" lg="10" class="admin-container">
                <v-card class="search-card" elevation="2">
                    <v-card-text>
                        <v-row align="center">
                            <v-col cols="12" md="4" sm="6">
                                <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Search Call"
                                    outlined dense hide-details background-color="white"
                                    class="search-field"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="4" sm="6" class="d-flex justify-center">
                                <v-chip-group v-model="selectedTypeIndex" mandatory active-class="primary--text"
                                    class="filter-chips">
                                    <v-chip v-for="filterOption in filterOptions" :key="filterOption.value"
                                        :value="filterOption.value" class="filter-chip" outlined
                                        @click="changeFilter(filterOption.value)">
                                        <v-icon left small>{{ filterOption.icon }}</v-icon>
                                        {{ filterOption.text }}
                                    </v-chip>
                                </v-chip-group>
                            </v-col>
                            <v-col cols="12" md="4" sm="6" class="d-flex align-center justify-center">
                                <div class="calls-count">
                                    <v-icon color="#0d2c6d" class="mr-2">mdi-phone-in-talk</v-icon>
                                    <span>Total <strong>{{ calls.length }}</strong> online calls</span>
                                </div>
                                <v-chip v-if="refreshing" color="primary" small class="ml-2 refresh-chip">
                                    <v-icon left small>mdi-refresh</v-icon>
                                    Refreshing...
                                </v-chip>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>

                <v-card class="data-table-card" elevation="2">
                    <v-data-table id="table1" :headers="headers" :items="calls" sort-by="call_duration" :search="search"
                        :footer-props="{
                            'items-per-page-options': [20, 50, 100, 500, 1000],
                            'items-per-page-text': 'Rows per page:'
                        }" class="calls-table" :loading="progressShow" loading-text="Loading data...">
                        <template v-if="$vuetify.breakpoint.mdAndUp" v-for="header in headers"
                            v-slot:[`header.${header.value}`]="{ header }">
                            <span class="table-header-text">{{ header.text }}</span>
                        </template>

                        <template v-if="$vuetify.breakpoint.mdAndUp" #item="{ item, index }">
                            <tr class="table-row">
                                <td class="table-cell">{{ index + 1 }}</td>
                                <td class="table-cell">
                                    <div class="caller-name">
                                        <v-icon small color="#0d2c6d" class="mr-1">mdi-account</v-icon>
                                        {{ item.callerName || 'Unknown' }}
                                    </div>
                                </td>
                                <td class="table-cell">
                                    <div class="caller-number">
                                        <v-icon small color="#0d2c6d" class="mr-1">mdi-phone</v-icon>
                                        {{ item.calleridnum }}
                                    </div>
                                </td>
                                <td class="table-cell">{{ item.accountcode }}</td>
                                <td class="table-cell">{{ item.ivrNumber }}</td>
                                <td class="table-cell">{{ formatContextName(item.context) }}</td>
                                <td class="table-cell">
                                    <v-chip small :color="getCallTypeColor(item.type)" text-color="white"
                                        class="call-type-chip">
                                        {{ item.type === "inbound" ? "Inbound" : "Outbound" }}
                                    </v-chip>
                                </td>
                                <td class="table-cell">
                                    <div class="duration-display">
                                        <v-icon small color="#0d2c6d" class="mr-1">mdi-clock-outline</v-icon>
                                        {{ item.duration }}
                                    </div>
                                </td>
                                <td class="table-cell">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on }">
                                            <v-btn icon small color="error" v-on="on" @click="hangupCall(item, index)"
                                                class="hangup-button">
                                                <v-icon>mdi-phone-hangup</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Hang up call</span>
                                    </v-tooltip>
                                </td>
                            </tr>
                        </template>

                        <template v-slot:item.actions="{ item, index }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <v-btn icon small color="error" v-on="on" @click="hangupCall(item, index)"
                                        class="hangup-button">
                                        <v-icon>mdi-phone-hangup</v-icon>
                                    </v-btn>
                                </template>
                                <span>Hang up call</span>
                            </v-tooltip>
                        </template>

                        <template v-slot:no-data>
                            <v-row v-if="progressShow" class="my-5">
                                <v-col class="d-flex justify-center">
                                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                                </v-col>
                            </v-row>
                            <div class="empty-state">
                                <v-icon size="64" color="#0d2c6d">mdi-phone-off</v-icon>
                                <span class="empty-text">No active calls found</span>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>
    </div>
</template>

<script>
import ApiServices from '@/services/api.service'
import SnackBar from '@/components/widgets/snackBar.vue'
import utils from '@/util/utils'
import Auth from '@/services/auth.service.js'

export default {
    components: {
        SnackBar,
    },
    data: () => ({
        snackbar: false,
        snackbarColorBt: "green",
        snacbarText: "",
        lineNumber: "",
        progressShow: false,
        refreshing: false,
        search: "",
        intervalId: null, // Add a property to store the interval ID
        calls: [],
        type: "all",
        selectedTypeIndex: 0,
        filterOptions: [
            { text: 'All Calls', value: 'all', icon: 'mdi-phone' },
            { text: 'Inbound', value: 'inbound', icon: 'mdi-phone-incoming' },
            { text: 'Outbound', value: 'outbound', icon: 'mdi-phone-outgoing' }
        ],
        headers: [
            { text: '#', value: "index", sortable: false, width: '50px' },
            { text: 'Name', value: 'callerName', align: 'center' },
            { text: 'Caller Number', value: 'calleridnum', align: 'center' },
            { text: 'Destination', value: 'accountcode', align: 'center' },
            { text: 'IVR Number', value: 'ivrNumber', align: 'center' },
            { text: 'Extension/Context', value: 'context', align: 'center' },
            { text: 'Type', value: 'type', width: '120px', align: 'center' },
            { text: 'Duration', value: 'duration', width: '120px', align: 'center' },
            { text: '', value: 'actions', sortable: false, width: '80px', align: 'center' },
        ],
        contexts: {
            "Ad": "Ad",
            "welcome": "Welcome",
            "welcome-after-Ad": "Welcome after Ad"
        }
    }),
    methods: {
        changeFilter(newType) {
            this.type = newType;
            this.progressShow = true;
            this.getOnlineCalls();
        },
        formatContextName(context) {
            return context === "welcome" ? "Welcome" : 
                   context === "main" ? "Main Extension" : 
                   context === "Ad" ? "Ad" : 
                   context === "welcome-after-Ad" ? "Welcome after Ad" :
                   context === "main-7-auth" ? "Extension-Auth-Entry-7" : 
                   context.includes("main-") ? context.replace("main", "Extension ") : context
        },
        getCallTypeColor(type) {
            return type === "inbound" ? "#4caf50" : "#ff9800";
        },

        async getOnlineCalls() {
            try {
                this.refreshing = true;
                let token = localStorage.getItem("token");
                let api = process.env.VUE_APP_BASE_URL + `/online_calls/get_online_calls?type=${this.type}`;
                const res = await fetch(api, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;
                this.refreshing = false;

                if (res.status >= 400) {
                    this.showSnackBar("Error receiving data", "red");
                } else if (res.status === 200) {
                    this.calls = jsonObject;
                }
            } catch (error) {
                this.refreshing = false;
                this.showSnackBar("Error get files: " + error, "red");
            }
        },

        formatDuration(seconds) {
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const sec = seconds % 60;

            const paddedHours = hours.toString().padStart(2, '0');
            const paddedMinutes = minutes.toString().padStart(2, '0');
            const paddedSeconds = sec.toString().padStart(2, '0');

            return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
        },

        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },

        async hangupCall(item, index) {
            try {
                this.progressShow = true;
                let token = localStorage.getItem("token");
                const channelJSON = JSON.stringify({ channel: item.channel });
                let api = process.env.VUE_APP_BASE_URL + `/online_calls/hangup_call`;
                const res = await fetch(api, ApiServices.requestOptions("POST", channelJSON, token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("Error hanging up call", "red");
                } else if (res.status === 200) {
                    // Use splice to remove the item at the specified index
                    const index = this.calls.findIndex(call => call.channel === item.channel);
                    this.calls.splice(index, 1);
                    this.showSnackBar("Call hung up successfully", "green");
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("Error hangup call: " + error, "red");
            }
        }
    },
    mounted() {
        this.lineNumber = Auth.getLineNumber();
        this.progressShow = true;
        this.getOnlineCalls();

        // Set up interval to refresh data every 5 seconds
        this.intervalId = setInterval(() => {
            this.getOnlineCalls();
        }, 5000);
    },
    beforeDestroy() {
        // Clear the interval when the component is destroyed
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    },
}
</script>

<style scoped>
.online-calls-container {
    font-family: 'Heebo', sans-serif;
    direction: ltr; /* Changed from default/rtl to ltr */
}

.search-card {
    margin-bottom: 24px;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
}

.search-card:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.search-field {
    border-radius: 8px;
}

.filter-chips {
    gap: 8px;
}

.filter-chip {
    font-weight: 600;
    font-size: 14px;
    height: 40px;
    padding: 0 16px;
    border: 2px solid #e0e0e0;
    transition: all 0.3s ease;
}

.filter-chip:hover {
    border-color: #0d2c6d;
    background-color: rgba(13, 44, 109, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.filter-chip.v-chip--active {
    border-color: #0d2c6d;
    background-color: rgba(13, 44, 109, 0.05);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.calls-count {
    display: flex;
    align-items: center;
    font-size: 16px;
    color: #0d2c6d;
}

.refresh-chip {
    margin-right: 16px;
    animation: pulse 1.5s infinite;
}

@keyframes pulse {
    0% {
        opacity: 0.6;
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0.6;
    }
}

.data-table-card {
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
    margin-bottom: 40px;
}

.data-table-card:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1) !important;
}

.calls-table {
    width: 100%;
}

.table-header-text {
    font-size: 16px;
    font-weight: 600;
    color: #0d2c6d;
}

.table-row {
    height: 60px !important;
    transition: background-color 0.2s ease;
}

.table-row:hover {
    background-color: rgba(13, 44, 109, 0.05) !important;
}

.table-cell {
    font-size: 14px;
    color: #333;
    text-align: center !important;
}

.caller-name,
.caller-number,
.duration-display {
    display: flex;
    align-items: center;
    justify-content: center;
}

.call-type-chip {
    font-weight: 600;
}

.hangup-button {
    transition: all 0.2s ease;
}

.hangup-button:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
}

.empty-text {
    font-size: 20px;
    font-weight: 600;
    color: #0d2c6d;
    margin-top: 16px;
}

/* Responsive adjustments */
@media (max-width: 960px) {
    .online-calls-container {
        padding: 0 12px;
    }

    .table-header-text {
        font-size: 14px;
    }

    .table-cell {
        font-size: 13px;
    }
}

@media (max-width: 600px) {

    .search-card,
    .data-table-card {
        border-radius: 8px;
    }

    .calls-count {
        justify-content: center;
        margin-top: 8px;
    }
}
</style>